import { LinkedinSettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import { LinkedinRecommendationKey, StageType } from '$extensionSrc/utils/enums'

import { LINKEDIN_AUTOMATION_STAGE_STATUSES } from './linkedinAutomationStageStatuses'
import {
  dataUsageGenerativeAiUrl,
  dataUsageSocialEconomicResearchUrl,
} from './linkedinUrls'
import { StageDef } from '../automationMachineInterfaces'
import {
  getCurrentValueForCheckbox,
  saveCurrentValueForCheckbox,
  toggleCheckbox,
} from '../checkboxDetection'
import {
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { shouldUpdateSetting } from '../utils'

class OptOutUnavailableException extends Error {
  static exceptionName = 'OptOutUnavailableException'

  constructor(message) {
    super(message)
    this.name = OptOutUnavailableException.exceptionName
  }
}

// We expect the "gather" to provide booleans to `initialValues`
// (true for opted in)

const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => (value ? 'On' : 'Off'))
const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => value === false)
const getCantUndoFn = () => defaultCantUndoFn
const getIsDisabledFn = (key) => (automationErrors) =>
  Boolean(automationErrors?.gatherError?.[key])
const getIsDisabledReasonFn = (key) => (automationErrors) =>
  automationErrors?.gatherError?.[key]?.message
    ? 'This setting is not available in your region'
    : null

const settingsLabels: Partial<Record<LinkedinSettingKey, string>> = {
  [LinkedinSettingKey.GENERATIVE_AI_TRAINING]:
    'Use my data for training content creation AI models',
  [LinkedinSettingKey.SOCIAL_ECONOMIC_RESEARCH]: 'Use data for research',
}

const settingsUrls: Partial<Record<LinkedinSettingKey, string>> = {
  [LinkedinSettingKey.GENERATIVE_AI_TRAINING]: dataUsageGenerativeAiUrl,
  [LinkedinSettingKey.SOCIAL_ECONOMIC_RESEARCH]:
    dataUsageSocialEconomicResearchUrl,
}

const failUrls: Partial<Record<LinkedinSettingKey, string | null>> = {
  [LinkedinSettingKey.GENERATIVE_AI_TRAINING]:
    'https://www.linkedin.com/mypreferences/gen-ai-page-not-authorized',
  [LinkedinSettingKey.SOCIAL_ECONOMIC_RESEARCH]: null,
}

function _getEitherOrUrlPattern(urlA: string, urlB: string | null): string {
  return urlB ? `(${urlA}|${urlB})($|\\?.+)` : `${urlA}($|\\?.+)`
}

const gatherStageDefs: StageDef[] = Object.entries(settingsUrls).map(
  ([settingsKey, url]) => {
    const failUrl = failUrls[settingsKey]

    return {
      type: StageType.ACTION,
      key: `GATHER_LINKEDIN_AI_RESEARCH_${settingsKey}`,
      status: LINKEDIN_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
      url,
      urlPattern: _getEitherOrUrlPattern(url, failUrl),
      action: async (_machineContext, userContext) => {
        try {
          if (failUrl && window.location.href.startsWith(failUrl)) {
            throw new OptOutUnavailableException(settingsKey)
          }

          await saveCurrentValueForCheckbox(
            userContext.updateStoredAutomationErrors,
            userContext.updateStoredInitialValues,
            settingsLabels,
            settingsKey,
            LinkedinRecommendationKey.LINKEDIN_AI_RESEARCH_OPT_OUT,
            'p',
          )
        } catch (error) {
          if (error.name === OptOutUnavailableException.exceptionName) {
            await userContext.updateStoredAutomationErrors(
              {
                [settingsKey]: {
                  gatherError: { name: error.name, message: error.message },
                },
              },
              LinkedinRecommendationKey.LINKEDIN_AI_RESEARCH_OPT_OUT,
            )
            // If initial values is empty, recommendation card will show as
            // gather never having been run, even though that isn't true
            await userContext.updateStoredInitialValues(
              {
                [settingsKey]: null,
              },
              LinkedinRecommendationKey.LINKEDIN_AI_RESEARCH_OPT_OUT,
            )
          } else {
            throw error
          }
        }
      },
    }
  },
)

const updateStageDefs: StageDef[] = Object.entries(settingsUrls).map(
  ([settingsKey, url]) => {
    const failUrl = failUrls[settingsKey]

    return {
      type: StageType.ACTION,
      key: `UPDATE_LINKEDIN_AI_RESEARCH_${settingsKey}`,
      status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
      url,
      urlPattern: _getEitherOrUrlPattern(url, failUrl),
      action: async (_machineContext, userContext) => {
        const { isDry, settingsSelected, initialValues } = userContext

        const shouldUpdate = shouldUpdateSetting(
          settingsSelected,
          initialValues,
          settingsKey,
          getIsFulfilledFn,
        )
        if (!shouldUpdate || isDry) {
          return
        }

        if (failUrl && window.location.href.startsWith(failUrl)) {
          throw new OptOutUnavailableException(settingsKey)
        }

        await toggleCheckbox(settingsLabels[settingsKey], 'p')
      },
    }
  },
)

const undoStageDefs: StageDef[] = Object.entries(settingsUrls).map(
  ([settingsKey, url]) => {
    const failUrl = failUrls[settingsKey]

    return {
      type: StageType.ACTION,
      key: `UNDO_LINKEDIN_AI_RESEARCH_${settingsKey}`,
      status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
      url,
      urlPattern: _getEitherOrUrlPattern(url, failUrl),
      action: async (_machineContext, userContext) => {
        const { initialValues, isDry, settingsSelected } = userContext

        if (
          !settingsSelected[settingsKey] ||
          initialValues[settingsKey] === null
        ) {
          return
        }

        if (failUrl && window.location.href.startsWith(failUrl)) {
          throw new OptOutUnavailableException(settingsKey)
        }

        const settingsLabel = settingsLabels[settingsKey]
        const currentSetting = await getCurrentValueForCheckbox(
          settingsLabel,
          'p',
        )

        if (currentSetting !== initialValues?.[settingsKey] && !isDry) {
          await toggleCheckbox(settingsLabels[settingsKey], 'p')
        }
      },
    }
  },
)

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
